/**
 * TestFeedbackScrubPanel
 *
 * Two-step preview → execute UI for wiping inbox-style rows that came
 * from test / QA visitors. Cousin to InactiveUserWipePanel but narrower
 * in scope — never touches users, payments, or reports.
 */
import { useState } from "react";
import { Loader2, Trash2, RefreshCw, AlertTriangle, ShieldCheck, Inbox } from "lucide-react";
import { toast } from "sonner";
import {
    adminTestFeedbackScrubPreview,
    adminTestFeedbackScrubExecute,
} from "@/lib/api";

export default function TestFeedbackScrubPanel() {
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [confirmText, setConfirmText] = useState("");

    const loadPreview = async () => {
        setLoadingPreview(true);
        try {
            const data = await adminTestFeedbackScrubPreview();
            setPreview(data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                e?.message ||
                "Could not load preview.",
            );
        } finally {
            setLoadingPreview(false);
        }
    };

    const execute = async () => {
        if (confirmText !== "WIPE") {
            toast.error('Type "WIPE" exactly to confirm.');
            return;
        }
        setExecuting(true);
        try {
            const data = await adminTestFeedbackScrubExecute();
            const t = (data?.total_primary_deleted || 0) +
                      (data?.total_cascade_deleted || 0);
            toast.success(`Removed ${t} test row${t === 1 ? "" : "s"}.`);
            setConfirmText("");
            setPreview(null);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                e?.message ||
                "Scrub failed.",
            );
        } finally {
            setExecuting(false);
        }
    };

    const totalPrimary = preview?.total_primary || 0;

    return (
        <section
            className="bl-card p-6 sm:p-8"
            data-testid="test-feedback-scrub-panel"
        >
            <div className="flex items-start gap-3 mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                    <Inbox className="w-5 h-5" />
                </span>
                <div>
                    <h2 className="font-display font-semibold text-xl text-bl">
                        Wipe Test Feedback &amp; Support
                    </h2>
                    <p className="mt-1 text-sm text-bl-muted leading-relaxed max-w-xl">
                        Permanently removes support tickets, contact-form
                        messages, and feedback rows submitted by test /
                        admin / QA visitors. Real-customer rows are never
                        in scope. Preview first, then confirm.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-5">
                <button
                    type="button"
                    onClick={loadPreview}
                    disabled={loadingPreview}
                    className="bl-btn-secondary text-sm gap-1.5"
                    data-testid="test-scrub-preview-btn"
                >
                    {loadingPreview ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Run preview
                </button>
            </div>

            {preview && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(preview.counts || {}).map(([k, v]) => (
                            <div
                                key={k}
                                className="rounded-lg border border-bl bg-bl-surface p-3"
                                data-testid={`scrub-count-${k}`}
                            >
                                <div className="text-xs uppercase tracking-wide text-bl-muted">
                                    {k.replace(/_/g, " ")}
                                </div>
                                <div className="mt-1 font-display font-semibold text-2xl text-bl">
                                    {v}
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPrimary > 0 && Object.entries(preview.samples || {}).map(([coll, rows]) => (
                        rows.length > 0 ? (
                            <details key={coll} className="text-sm">
                                <summary className="cursor-pointer text-bl-muted">
                                    Sample rows in {coll} (first {rows.length})
                                </summary>
                                <ul className="mt-2 space-y-1 pl-4 text-xs text-bl-muted font-mono">
                                    {rows.map((r, i) => (
                                        <li key={i}>
                                            {r.email || r.user_id || r.id || "—"}
                                            {r.subject ? ` · ${r.subject}` : ""}
                                            {r.created_at ? ` · ${r.created_at}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        ) : null
                    ))}

                    {totalPrimary === 0 ? (
                        <div
                            className="rounded-lg border border-dashed border-bl p-6 text-center text-sm text-bl-muted inline-flex items-center gap-2"
                            data-testid="scrub-clean"
                        >
                            <ShieldCheck className="w-4 h-4 text-[var(--bl-moss)]" />
                            Inboxes are clean — no test rows match.
                        </div>
                    ) : (
                        <div className="rounded-lg border border-[var(--bl-amber)] bg-[var(--bl-amber-tint)] p-4">
                            <div className="flex items-start gap-2 mb-3">
                                <AlertTriangle className="w-4 h-4 text-[var(--bl-amber)] mt-0.5" />
                                <p className="text-sm text-bl">
                                    Will permanently delete{" "}
                                    <span className="font-semibold">{totalPrimary}</span>{" "}
                                    primary row{totalPrimary === 1 ? "" : "s"} (and
                                    cascade messages where applicable).
                                </p>
                            </div>
                            <label className="block text-xs text-bl-muted mb-1">
                                Type <span className="font-mono">WIPE</span> to confirm:
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    className="bl-input text-sm py-2 font-mono w-40"
                                    placeholder="WIPE"
                                    data-testid="scrub-confirm-input"
                                />
                                <button
                                    type="button"
                                    onClick={execute}
                                    disabled={executing || confirmText !== "WIPE"}
                                    className="bl-btn-primary text-sm gap-1.5"
                                    data-testid="scrub-execute-btn"
                                >
                                    {executing ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-3.5 h-3.5" />
                                    )}
                                    Execute scrub
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
